import React, { useState, useEffect } from 'react';
import apiClient from '../config/axios';
import { useNavigate } from 'react-router-dom';
import './FormPage.css';

const FormPage = () => {
  const navigate = useNavigate();
  const [estates, setEstates] = useState([]);
  const [selectedEstate, setSelectedEstate] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    paymentType: '',
    amount: '',
    plotType: '' // '1plot' or 'miniplot'
  });
  const [loading, setLoading] = useState(false);
  const [loadingEstates, setLoadingEstates] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEstates();
  }, []);

  const fetchEstates = async () => {
    setLoadingEstates(true);
    setError('');
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await apiClient.get('/api/estates', {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('Estates loaded:', response.data);
      if (response.data && response.data.length > 0) {
        setEstates(response.data);
      } else {
        setError('No estates found. Please check if the server is running and database is initialized.');
      }
    } catch (err) {
      console.error('Error fetching estates:', err);
      setError(`Failed to load estates: ${err.message}. Make sure the backend server is running on port 5000.`);
    } finally {
      setLoadingEstates(false);
    }
  };

  const handleEstateClick = (estate) => {
    setSelectedEstate(estate);
    const estateProducts = estate.products || [];
    console.log(`Estate "${estate.name}" products:`, estateProducts.map(p => p.name));
    setProducts(estateProducts);
    setSelectedProduct(null);
    setFormData({ ...formData, amount: '', paymentType: '', plotType: '' });
  };

  // Check if product is Fractional Ownership
  const isFractionalOwnership = (productName) => {
    const nameLower = productName.toLowerCase();
    return nameLower.includes('fractional ownership');
  };

  // Check if product has mini plot options available
  const hasMiniPlotOptions = (product) => {
    return product.mini_plot_full_amount != null || product.mini_plot_installment_amount != null;
  };

  // Check if product has installment amount set (if null, allow custom input)
  const hasInstallmentAmount = (product) => {
    return product.installment_amount != null;
  };

  // List of products that have been updated with original prices
  // Note: Matching is flexible and handles variations like "The Jasper housing unit" vs "Jasper Housing Unit"
  const updatedProducts = [
    'Sardius Farm City Epe',
    'Beryl Polo Ibadan',
    'Beryl Estate Abeokuta',
    'Bery Estate Abeokuta', // Typo variant from backend
    'Beryl Abeokuta',
    'Beryl Beach Font Ibeju Lekki',
    'Jasper Smart City Isheri',
    'Garnet Ibeju Lekki',
    'Sapphire Gwagwalada Phase 3',
    'Gvest',
    'Gvest Estate',
    // 'Jasper Housing Unit',
    'The Jasper housing unit'
  ];

  // Normalize name for comparison (case-insensitive, remove "The" prefix, trim)
  const normalizeName = (name) => {
    if (!name) return '';
    return name.toLowerCase()
      .replace(/^the\s+/i, '') // Remove "The" prefix
      .trim();
  };

  // Check if estate has been updated (shows all products from updated estates)
  const isEstateUpdated = (estateName) => {
    if (!estateName) return false;
    const normalizedEstateName = normalizeName(estateName);
    
    return updatedProducts.some(updatedProduct => {
      const normalizedUpdated = normalizeName(updatedProduct);
      // Check for exact match or if one contains the other
      return normalizedEstateName === normalizedUpdated || 
             normalizedEstateName.includes(normalizedUpdated) ||
             normalizedUpdated.includes(normalizedEstateName);
    });
  };

  // Check if product has been updated
  const isProductUpdated = (productName, estateName = null) => {
    if (!productName) return false;
    
    const normalizedProductName = normalizeName(productName);
    const normalizedEstateName = estateName ? normalizeName(estateName) : '';
    
    // Special case: "Jasper Housing Unit" products should only show under "The Jasper housing unit" estate
    const isJasperHousingUnit = normalizedProductName.includes('jasper housing unit') || 
                                  normalizedProductName.includes('housing unit');
    if (isJasperHousingUnit) {
      // Only show if estate is "The Jasper housing unit" or "Jasper Housing Unit"
      const isJasperHousingUnitEstate = normalizedEstateName.includes('jasper housing unit') && 
                                         !normalizedEstateName.includes('smart city') &&
                                         !normalizedEstateName.includes('isheri');
      return isJasperHousingUnitEstate;
    }
    
    // If estate is updated, show all products from that estate (except Jasper Housing Unit which is handled above)
    if (estateName && isEstateUpdated(estateName)) {
      return true;
    }
    
    // Check for exact match or if product name contains/equals any updated product
    return updatedProducts.some(updatedProduct => {
      const normalizedUpdated = normalizeName(updatedProduct);
      
      // Skip "Jasper Housing Unit" in this check since it's handled above
      if (normalizedUpdated.includes('jasper housing unit') || normalizedUpdated.includes('housing unit')) {
        return false;
      }
      
      // Normalize "bery" and "beryl" to handle typos
      const normalizedProduct = normalizedProductName.replace(/bery(l)?/gi, 'bery');
      const normalizedUpdatedProd = normalizedUpdated.replace(/bery(l)?/gi, 'bery');
      
      // Check for exact match or if one contains the other
      return normalizedProduct === normalizedUpdatedProd || 
             normalizedProduct.includes(normalizedUpdatedProd) ||
             normalizedUpdatedProd.includes(normalizedProduct);
    });
  };

  const handlePaymentOptionClick = (product, paymentType, plotType) => {
    setSelectedProduct(product);
    const isOutright = paymentType === 'outright';
    const isInstallment = paymentType === 'installment';
    const is1Plot = plotType === '1plot';
    const isMiniPlot = plotType === 'miniplot';
    
    let amount = '';
    if (isOutright && is1Plot) {
      // 1 Plot Outright - use full_amount
      amount = product.full_amount || '';
    } else if (isOutright && isMiniPlot) {
      // Mini Plot Outright - use mini_plot_full_amount
      amount = product.mini_plot_full_amount || '';
    } else if (isInstallment && is1Plot) {
      // 1 Plot Installment - use installment_amount if available, otherwise allow custom input
      amount = product.installment_amount || '';
    } else if (isInstallment && isMiniPlot) {
      // Mini Plot Installment - use mini_plot_installment_amount if available
      amount = product.mini_plot_installment_amount || '';
    }
    
    setFormData({ 
      ...formData, 
      paymentType, 
      plotType,
      amount 
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!selectedEstate || !selectedProduct) {
      setError('Please select an estate and product');
      setLoading(false);
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phoneNumber || !formData.paymentType || !formData.amount) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    // Validate fractional ownership minimum amount
    if (formData.paymentType === 'fractional' && parseFloat(formData.amount) < 20000000) {
      setError('Minimum investment for fractional ownership is ₦20,000,000');
      setLoading(false);
      return;
    }

    try {
      // Map 'outright' to 'full' for backend compatibility
      // For fractional ownership, we'll send it as 'fractional' or map to 'full' if backend expects it
      const paymentTypeForBackend = formData.paymentType === 'outright' ? 'full' : 
                                     formData.paymentType === 'fractional' ? 'fractional' : 
                                     formData.paymentType;
      
      const response = await apiClient.post('/api/clients/submit', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        estateId: selectedEstate.id,
        productId: selectedProduct.id,
        paymentType: paymentTypeForBackend,
        amount: parseFloat(formData.amount)
      });

      // Store client ID and account number for receipt upload page
      localStorage.setItem('clientId', response.data.clientId);
      localStorage.setItem('accountNumber', response.data.accountNumber);
      
      navigate('/receipt-upload');
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.error || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-container">
        <h1>Estate Subscription Form</h1>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Select Estate *</label>
            {loadingEstates ? (
              <div style={{ padding: '20px', textAlign: 'center', border: '2px dashed #ddd', borderRadius: '8px' }}>
                <p style={{ fontSize: '16px', color: '#666' }}>Loading estates...</p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                  Make sure the backend server is running on port 5000
                </p>
              </div>
            ) : estates.length === 0 ? (
              <div className="error-message" style={{ padding: '20px' }}>
                <strong>No estates available.</strong>
                <p style={{ marginTop: '10px', marginBottom: '10px' }}>
                  This usually means:
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '15px' }}>
                  <li>The backend server is not running</li>
                  <li>The database hasn't been initialized</li>
                  <li>There's a connection issue</li>
                </ul>
                <p style={{ marginBottom: '10px' }}>
                  <strong>To fix:</strong> Run <code>npm run server</code> in the project root directory
                </p>
                <button 
                  type="button" 
                  onClick={fetchEstates}
                  style={{ marginTop: '10px', padding: '8px 16px' }}
                  className="btn btn-secondary"
                >
                  Retry Loading Estates
                </button>
              </div>
            ) : (
              <div className="estate-grid">
                {estates.map(estate => (
                  <div
                    key={estate.id}
                    className={`estate-card ${selectedEstate?.id === estate.id ? 'selected' : ''}`}
                    onClick={() => handleEstateClick(estate)}
                  >
                    {estate.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedEstate && products.length > 0 && (
            <div className="form-group">
              <label>Select Product *</label>
              <div className="product-list">
                {/* 
                  COMMENTED OUT: Only showing products that have been updated with original prices.
                  Updated products:
                  - Sardius Farm City Epe
                  - Beryl Polo Ibadan
                  - Beryl Estate Abeokuta
                  - Beryl Abeokuta
                  - Beryl Beach Font Ibeju Lekki
                  - Jasper Smart City Isheri
                  - Garnet Ibeju Lekki
                  - Sapphire Gwagwalada Phase 3
                  - Gvest
                  - Jasper Housing Unit
                  
                  All other products are temporarily hidden until their original prices are updated.
                  
                  ORIGINAL CODE (commented out - uncomment when all products are updated):
                  {products.map(product => {
                */}
                {products
                  .filter(product => {
                    const isUpdated = isProductUpdated(product.name, selectedEstate?.name);
                    if (!isUpdated) {
                      console.log(`Product "${product.name}" from estate "${selectedEstate?.name}" is filtered out (not in updated list)`);
                    }
                    return isUpdated;
                  })
                  .map(product => {
                  const isSelected = selectedProduct?.id === product.id;
                  
                  return (
                    <div key={product.id} className="product-item-container">
                      <div 
                        className={`product-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (!isSelected) {
                            setSelectedProduct(product);
                            if (isFractionalOwnership(product.name)) {
                              // For fractional ownership, set amount to 20 million and paymentType to 'fractional'
                              setFormData({ ...formData, paymentType: 'fractional', plotType: '', amount: '20000000' });
                            } else {
                              setFormData({ ...formData, paymentType: '', plotType: '', amount: '' });
                            }
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <strong>{product.name}</strong>
                      </div>
                      {isSelected && !isFractionalOwnership(product.name) && (
                        <div className="payment-options" style={{ marginTop: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: hasMiniPlotOptions(product) ? '1fr 1fr' : '1fr', 
                            gap: '10px' 
                          }}>
                            <button
                              type="button"
                              className={`payment-option-btn ${formData.paymentType === 'outright' && formData.plotType === '1plot' ? 'active' : ''}`}
                              onClick={() => handlePaymentOptionClick(product, 'outright', '1plot')}
                              style={{
                                padding: '10px',
                                border: '2px solid',
                                borderColor: formData.paymentType === 'outright' && formData.plotType === '1plot' ? '#007bff' : '#ddd',
                                borderRadius: '8px',
                                backgroundColor: formData.paymentType === 'outright' && formData.plotType === '1plot' ? '#e7f3ff' : '#fff',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              1 Plot Outright
                            </button>
                            {hasMiniPlotOptions(product) && (
                              <button
                                type="button"
                                className={`payment-option-btn ${formData.paymentType === 'outright' && formData.plotType === 'miniplot' ? 'active' : ''}`}
                                onClick={() => handlePaymentOptionClick(product, 'outright', 'miniplot')}
                                style={{
                                  padding: '10px',
                                  border: '2px solid',
                                  borderColor: formData.paymentType === 'outright' && formData.plotType === 'miniplot' ? '#007bff' : '#ddd',
                                  borderRadius: '8px',
                                  backgroundColor: formData.paymentType === 'outright' && formData.plotType === 'miniplot' ? '#e7f3ff' : '#fff',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                Mini Plot Outright
                              </button>
                            )}
                            <button
                              type="button"
                              className={`payment-option-btn ${formData.paymentType === 'installment' && formData.plotType === '1plot' ? 'active' : ''}`}
                              onClick={() => handlePaymentOptionClick(product, 'installment', '1plot')}
                              style={{
                                padding: '10px',
                                border: '2px solid',
                                borderColor: formData.paymentType === 'installment' && formData.plotType === '1plot' ? '#007bff' : '#ddd',
                                borderRadius: '8px',
                                backgroundColor: formData.paymentType === 'installment' && formData.plotType === '1plot' ? '#e7f3ff' : '#fff',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              1 Plot Installment
                            </button>
                            {hasMiniPlotOptions(product) && (
                              <button
                                type="button"
                                className={`payment-option-btn ${formData.paymentType === 'installment' && formData.plotType === 'miniplot' ? 'active' : ''}`}
                                onClick={() => handlePaymentOptionClick(product, 'installment', 'miniplot')}
                                style={{
                                  padding: '10px',
                                  border: '2px solid',
                                  borderColor: formData.paymentType === 'installment' && formData.plotType === 'miniplot' ? '#007bff' : '#ddd',
                                  borderRadius: '8px',
                                  backgroundColor: formData.paymentType === 'installment' && formData.plotType === 'miniplot' ? '#e7f3ff' : '#fff',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                Mini Plot Installment
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {isSelected && isFractionalOwnership(product.name) && (
                        <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                            Fractional Ownership of Jasper Housing Unit
                          </p>
                          <p style={{ fontSize: '12px', color: '#666' }}>
                            Enter the amount you wish to invest (minimum: ₦20,000,000)
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedProduct && formData.paymentType && (
            <div className="form-group">
              <label htmlFor="amount">Amount *</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                required
                min={formData.paymentType === 'fractional' ? '20000000' : undefined}
                readOnly={formData.paymentType === 'outright'}
                placeholder={
                  formData.paymentType === 'fractional' 
                    ? 'Enter amount (minimum: ₦20,000,000)' 
                    : formData.paymentType === 'installment' 
                    ? 'Enter installment amount' 
                    : ''
                }
                style={{
                  backgroundColor: formData.paymentType === 'outright' ? '#f5f5f5' : '#fff',
                  cursor: formData.paymentType === 'outright' ? 'not-allowed' : 'text'
                }}
              />
              {formData.paymentType === 'outright' && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Amount is fixed for outright payment
                </p>
              )}
              {formData.paymentType === 'installment' && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {hasInstallmentAmount(selectedProduct) 
                    ? 'Enter your desired installment amount' 
                    : 'Enter your custom installment amount'}
                </p>
              )}
              {formData.paymentType === 'fractional' && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Minimum investment: ₦20,000,000. This represents your fractional ownership of the Jasper Housing Unit.
                </p>
              )}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormPage;
